import React, { memo, useCallback, useEffect } from 'react'
import tw from 'twin.macro'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Typography } from '@mui/material'
import { SectionCardSkeleton } from 'src/components/shared/SectionCardSkeleton'
import { CustomerSettingsForm } from 'src/components/customer/settings/settings-form/CustomerSettingsForm'
import { toSavePayload } from 'src/components/customer/settings/settings-form/CustomerSettingsForm.mapper'
import type { CustomerSettingsFormValues } from 'src/components/customer/settings/settings-form/CustomerSettingsForm.interface'
import {
  AdminFetchDocusignPartnerTemplates,
  AdminFetchGigsafeConfig,
  AdminSaveGigsafeConfig,
} from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import {
  getGigsafeConfig,
  getGigsafeConfigError,
  getGigsafeConfigLoaded,
  getGigsafeConfigPartnerId,
} from 'src/selectors/gigsafeConfig'

const CustomerSettingsPageComponent = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const snackbar = useSnackbar()

  const { isAuthenticated } = useAuthSession()
  const config = useSelector(getGigsafeConfig)
  const isLoaded = useSelector(getGigsafeConfigLoaded)
  const error = useSelector(getGigsafeConfigError)
  const loadedPartnerId = useSelector(getGigsafeConfigPartnerId)
  const isReady = isLoaded && loadedPartnerId === customerId
  // Save is POST/PUT /partners/gigsafe-config — `partner_settings_manage`. Without it the form is
  // shown read-only (ENG-1207 part 2).
  const canManageSettings = useHasPermission(InternalPermission.PartnerSettingsManage)
  // The Master Contractor template options come from the DocuSign GET, a different permission.
  // Without it the fetch is skipped and the select offers only the stored value (spec §6).
  const canViewDocusignTemplates = useHasPermission(InternalPermission.DocusignTemplatesView)

  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchGigsafeConfig.request({ partnerId: customerId }))

    if (canViewDocusignTemplates) {
      asyncDispatchWithErrorToast(
        AdminFetchDocusignPartnerTemplates.request({ partnerId: customerId }),
      )
    }
  }, [isAuthenticated, customerId, canViewDocusignTemplates, asyncDispatchWithErrorToast])

  const handleSubmit = useCallback(
    async (values: CustomerSettingsFormValues) => {
      if (!customerId) {
        return
      }

      const saved = await asyncDispatchWithErrorToast(
        AdminSaveGigsafeConfig.request(toSavePayload(customerId, values)),
      )

      if (saved) {
        snackbar?.enqueueSnackbar('Customer settings updated', { variant: 'success' })
      }
    },
    [customerId, asyncDispatchWithErrorToast, snackbar],
  )

  if (!customerId) {
    return null
  }

  return (
    <div data-testid="admin-customer-settings-page" css={tw`mx-auto w-full max-w-[720px] p-6`}>
      <Typography variant="h2" css={tw`mb-5`}>
        Settings
      </Typography>

      {error && !isReady ? (
        <Typography variant="body2" css={tw`text-critical-500`}>
          {error}
        </Typography>
      ) : null}

      {!isReady && !error ? (
        <SectionCardSkeleton fields={4} dataTestId="admin-customer-settings-loading" />
      ) : null}

      {isReady ? (
        <CustomerSettingsForm
          config={config}
          isReadOnly={!canManageSettings}
          onSubmit={handleSubmit}
        />
      ) : null}
    </div>
  )
}

export const CustomerSettingsPage = memo(CustomerSettingsPageComponent)
